/**
 * src/lib/offline/networkStatus.js — single source of truth for connectivity.
 *
 * The whole offline-tasks feature keys off one question: are we online right
 * now? `navigator.onLine` answers it for reads; the `online`/`offline` window
 * events drive push notifications to subscribers (sync engine, status hooks).
 *
 * Mirrors the existing online/offline handling in OfflineBanner.jsx /
 * useQueueStatus.js, but centralized so every consumer agrees on the state
 * and we register the window listeners exactly once.
 *
 * FORCED-OFFLINE PREVIEW (2026-06-12): the CEO wanted an "offline mode" toggle
 * to verify the cached tabs render correctly WITHOUT enabling airplane mode.
 * setForcedOffline(true) makes getOnline() report offline app-wide, so every
 * domain hook hydrates from its cache and every mutation queues to the outbox —
 * exactly as on a plane. It is persisted so the preview holds across a reload.
 */

const listeners = new Set();

// Manual offline override (preview toggle). Persisted so the simulated-offline
// state survives a reload — the whole point is to reproduce the cold-boot path.
const FORCE_OFFLINE_KEY = 'acn_force_offline';
let forcedOffline = readForcedOffline(
  typeof localStorage !== 'undefined' ? localStorage : undefined,
  typeof location !== 'undefined' ? location.search : '',
);

/**
 * Read the persisted preview flag. TICKET-1399 escape hatch: opening the app with
 * `?offline=0` clears a preview left ON, so a stuck boot never needs the UI to undo it.
 * @param {Storage|undefined} storage
 * @param {string} [search] location.search
 */
export function readForcedOffline(storage, search = '') {
  try {
    if (!storage) return false;
    if (/[?&]offline=0(&|$)/.test(search || '')) {
      storage.removeItem(FORCE_OFFLINE_KEY);
      return false;
    }
    return storage.getItem(FORCE_OFFLINE_KEY) === '1';
  } catch {
    // spine-emit-ok: localStorage read in private mode — default to online, no signal to log
    return false;
  }
}

/** Effective connectivity = forced-offline wins, else the live navigator reading. */
function currentEffective() {
  if (forcedOffline) return false;
  if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
    return navigator.onLine;
  }
  return online;
}

// `online` is the tracked EFFECTIVE mirror (after the forced-offline override),
// used both as the no-double-fire dedup and the non-browser fallback.
let online = typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
  ? (forcedOffline ? false : navigator.onLine)
  : !forcedOffline;

function broadcast() {
  const eff = currentEffective();
  if (online === eff) return; // no double-fire on unchanged state
  online = eff;
  for (const cb of listeners) {
    try { cb(online); } catch { /* a bad subscriber must not wedge the rest */ }
  }
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('online', broadcast);
  window.addEventListener('offline', broadcast);
}

/**
 * Current connectivity. The forced-offline override wins over everything so the
 * preview toggle reproduces a plane app-wide. Otherwise prefers the live
 * `navigator.onLine` reading, falling back to the tracked mirror in
 * non-browser/test environments.
 */
export function getOnline() {
  if (forcedOffline) return false;
  if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
    return navigator.onLine;
  }
  return online;
}

/** True when the user has manually forced offline-preview mode. */
export function isForcedOffline() {
  return forcedOffline;
}

/**
 * Toggle forced offline-preview mode. Persists the choice and notifies every
 * subscriber of the new EFFECTIVE state so views re-hydrate from cache (or
 * re-fetch live) immediately — no reload required.
 * @param {boolean} value
 */
export function setForcedOffline(value) {
  forcedOffline = !!value;
  try {
    if (typeof localStorage !== 'undefined') {
      if (forcedOffline) localStorage.setItem(FORCE_OFFLINE_KEY, '1');
      else localStorage.removeItem(FORCE_OFFLINE_KEY);
    }
  } catch { /* spine-emit-ok: localStorage write in private mode — override still holds in-memory */ }
  broadcast();
}

/**
 * TICKET-1398 — make the preview a REAL plane. Flipping getOnline() alone was not
 * enough: apiFetch, supabase-js and ~40 raw fetch sites never ask getOnline(), so
 * during a preview the deal detail still loaded live (2026-10-25 telemetry:
 * voice.list / leads.thread answered 200 mid-preview) and the preview passed
 * while a real flight showed empty views. While preview is ON this guard fails
 * network requests the way WebKit does offline (TypeError "Load failed"), so the
 * app takes exactly the code paths it takes in the air.
 *
 * Still allowed through: supabase auth (/auth/v1/, TICKET-1399), telemetry inserts (so a preview session stays
 * diagnosable — on a real plane they are lost), same-origin static assets (the
 * service worker serves those on a plane), and blob:/data: URLs (local).
 */
const PREVIEW_GUARD = Symbol.for('acn.previewFetchGuard');

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  if (input && typeof input.href === 'string') return input.href;
  return String(input ?? '');
}

function previewLetsThrough(rawUrl, origin) {
  if (/^(blob|data):/i.test(rawUrl)) return true;
  let u;
  try { u = new URL(rawUrl, origin || 'http://localhost'); } catch { /* spine-emit-ok: unparseable URL during a preview is blocked, same as on a plane */ return false; }
  if (/\/rest\/v1\/app_error_logs(\?|$)/.test(u.pathname + u.search)) return true;
  // TICKET-1399: auth (token refresh, user) must pass. Blocking it hung boot on
  // "loading" forever when a preview was left ON across a reload (prod rollback
  // 2026-10-25). It loads no business data, so the preview stays honest.
  if (u.pathname.startsWith('/auth/v1/')) return true;
  const sameOrigin = !origin || u.origin === origin;
  return sameOrigin && !u.pathname.startsWith('/api/');
}

/**
 * Wrap target.fetch once so a forced-offline preview blocks real requests.
 * Idempotent; checks the preview flag at CALL time, so toggling needs no reload.
 * @param {{ fetch: Function, location?: { origin?: string } }} [target]
 */
export function installPreviewFetchGuard(target = typeof globalThis !== 'undefined' ? globalThis : undefined) {
  if (!target || typeof target.fetch !== 'function' || target.fetch[PREVIEW_GUARD]) return;
  const nativeFetch = target.fetch;
  const guarded = function previewAwareFetch(input, init) {
    if (forcedOffline && !previewLetsThrough(requestUrl(input), target.location?.origin)) {
      return Promise.reject(new TypeError('Load failed (offline preview)'));
    }
    return nativeFetch.call(target, input, init);
  };
  guarded[PREVIEW_GUARD] = true;
  target.fetch = guarded;
}

if (typeof window !== 'undefined') installPreviewFetchGuard(window);

/**
 * Subscribe to connectivity changes. Returns an unsubscribe function.
 * @param {(online: boolean) => void} cb
 */
export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Test helper — force the tracked mirror without touching navigator. */
export function __setOnlineForTest(value) {
  online = value;
}

/** Test helper — reset the forced-offline override (in-memory only). */
export function __resetForcedOfflineForTest() {
  forcedOffline = false;
}
